import { PlayerServiceAPI } from '@API/data-services/player-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { LoadingRows } from '@Components/util/loading-rows'
import { formatDateToTimeZone } from '@Components/util/utils'
import {
  CouponAssignmentStatus,
  CouponAssignmentWithPromotion,
} from '@Generated/back-office-client/src'
import { Badge, Container, Flex, Heading, Skeleton, Table, Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CouponBarcode } from '@Components/tools/player/coupon-barcode.tsx'
import { SideDrawer } from '@Components/common/side-drawer.tsx'

export const CouponHistory = () => {
  const params = useParams()
  const playerLookupId = params?.lookupId
  const [showCouponDetails, setShowCouponDetails] = useState<
    CouponAssignmentWithPromotion | undefined
  >()

  const { data: couponsHistory, isLoading: areCouponsLoading } = useQuery({
    queryKey: [`${playerLookupId}-coupons`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return PlayerServiceAPI.getPlayerCoupons(playerLookupId!)
    },
    enabled: !!playerLookupId,
  })

  const getCouponStatusColor = (
    coupon: CouponAssignmentWithPromotion,
  ): 'red' | 'green' | 'gray' | 'yellow' => {
    if (coupon.couponExpiresAt < new Date()) {
      return 'red'
    } else if (coupon.status === CouponAssignmentStatus.ASSIGNED) {
      return 'green'
    } else if (coupon.status === CouponAssignmentStatus.REDEEMED) {
      return 'gray'
    } else {
      return 'yellow'
    }
  }

  const getCouponStatusText = (coupon: CouponAssignmentWithPromotion): string => {
    if (coupon.couponExpiresAt < new Date()) {
      return 'Expired'
    } else if (coupon.status === CouponAssignmentStatus.ASSIGNED) {
      return 'Assigned'
    } else if (coupon.status === CouponAssignmentStatus.REDEEMED) {
      return 'Redeemed'
    } else {
      return 'N/A'
    }
  }

  const mapCoupons = useCallback(() => {
    return couponsHistory?.map((coupon) => {
      return (
        <Table.Row key={coupon.createdAt.getTime()}>
          <Table.RowHeaderCell className="coupon-cell">{coupon.promotionName}</Table.RowHeaderCell>
          <Table.Cell className="coupon-cell">
            <a className="open-scan-details" onClick={() => setShowCouponDetails(coupon)}>
              {coupon.couponId}
            </a>
          </Table.Cell>
          <Table.Cell className="coupon-cell">{formatDateToTimeZone(coupon.createdAt)}</Table.Cell>
          <Table.Cell className="coupon-cell">
            {formatDateToTimeZone(coupon.couponExpiresAt)}
          </Table.Cell>
          <Table.Cell className="coupon-cell">
            {coupon.couponRedeemedAt ? formatDateToTimeZone(coupon.couponRedeemedAt) : '--'}
          </Table.Cell>
          <Table.Cell className="coupon-cell">
            <Badge className="coupon-status-badge" color={getCouponStatusColor(coupon)}>
              {getCouponStatusText(coupon)}
            </Badge>
          </Table.Cell>
          <Table.Cell className="coupon-cell">
            <CouponBarcode coupon={coupon}></CouponBarcode>
          </Table.Cell>
        </Table.Row>
      )
    })
  }, [couponsHistory])

  return (
    <Container>
      <Flex direction="column" gap="3">
        <Heading>Coupon History</Heading>
        <Text>{playerLookupId}</Text>
        <Skeleton loading={areCouponsLoading}>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Promotion Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Transaction ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Assigned Date</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Expiration Date</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Redemption Date</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Barcode Reference</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>{areCouponsLoading ? <LoadingRows /> : mapCoupons()}</Table.Body>
          </Table.Root>
        </Skeleton>
      </Flex>
      {showCouponDetails ? (
        <SideDrawer onClose={() => setShowCouponDetails(undefined)}>
          <Heading as="h3" size="4">
            Coupon Preview
          </Heading>
          <Flex direction="column" justify="between">
            <img
              src={showCouponDetails.templateImageUrl}
              alt="coupon-image"
              className="coupon-image"
            />
            {showCouponDetails.promotionDescription ? (
              <Text>{showCouponDetails.promotionDescription}</Text>
            ) : null}
          </Flex>
        </SideDrawer>
      ) : null}
    </Container>
  )
}
